import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

import { NEW_FORMAT, FORMAT } from 'constants/date-constants';
import { GetTotemClosedDaysQuery, Maybe, OrderState, Scalars } from 'data/__generated__';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

/**
 * @param {String} time - In the format "HH:MM"
 */
export const getFrenchFormattedTime = (time: string | undefined): string => {
    if (!time) {
        throw new Error('No time provided');
    }

    const [hours, minutes] = time.split(':');

    if (Number.isNaN(Number(hours)) || Number.isNaN(Number(minutes))) {
        throw new Error('Parameter is not a number!');
    }

    const stringHours = Number(hours).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });

    const stringMinutes = Number(minutes).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });

    return `${stringHours}h${stringMinutes}`;
};

export const getDeliveryHourRangeString = (startHour: string | undefined, stopHour: string | undefined): string => {
    const formattedStartHour = getFrenchFormattedTime(startHour);
    const formattedStopHour = getFrenchFormattedTime(stopHour);

    if (startHour && stopHour) {
        return `entre ${formattedStartHour} et ${formattedStopHour}`;
    }

    return startHour ? `à partir de ${formattedStartHour}` : `avant ${formattedStopHour}`;
};

export function getDaysToPreviousWeekday(day: number): number {
    switch (day) {
        case 0:
            return 2; // Sunday last weekday is Friday (j-2)
        case 1:
            return 3; // Monday last weekday is Friday (j-3)
        case 2: // Tuesday - Saturday last weekday is day before (j-1)
        case 3:
        case 4:
        case 5:
        case 6:
            return 1;
        default:
            throw new Error('Day input must be between 0-6');
    }
    // ...existing code...
}
function getFormattedPreviousWeekday(date: dayjs.Dayjs): string {
    const dateWeekday = getDateWeekday(date);
    return date.subtract(getDaysToPreviousWeekday(dateWeekday), 'day').format(NEW_FORMAT);
}

function getDateWeekday(date: Date | dayjs.Dayjs | string): number {
    if (typeof date === 'string' || date instanceof Date) {
        return new Date(date).getDay();
    } else if (typeof (date as any).day === 'function') {
        return (date as dayjs.Dayjs).day();
    }
    return 0;
}

// Can modify until preparation date - 1 DAY (not weekday),
// Preparation date = order date - 1 weekday
// If -1 weekday preparation date is bank holiday, preparation = order date - 2 weekdays

export interface RemainingTimeForValidationParams {
    deliveryDate: string;
    firstOrder: Maybe<Scalars['Boolean']['output']> | null;
    isPunctual: boolean;
    state: OrderState | 'Created' | 'InProgress' | 'Validated' | ''; // or use OrderState type
    hasBeenValidated: boolean;
    totemClosedDays: (string | Date)[];
}

export function remainingTimeForValidation({
    deliveryDate,
    firstOrder,
    hasBeenValidated,
    isPunctual,
    state,
    totemClosedDays,
}: RemainingTimeForValidationParams): {
    validationLimitDate: dayjs.Dayjs;
    fullString: string;
    validationLimitDateIsPast: boolean;
} {
    const dayJsDeliveryDate = dayjs(deliveryDate, FORMAT);
    const deliveryDateWeekday = getDateWeekday(dayJsDeliveryDate);

    let preparationFormattedDate = getFormattedPreviousWeekday(dayJsDeliveryDate);
    let preparationDelays = getDaysToPreviousWeekday(deliveryDateWeekday);

    while (totemClosedDays.includes(preparationFormattedDate)) {
        const preparationDate = dayjs(preparationFormattedDate, NEW_FORMAT);
        preparationFormattedDate = getFormattedPreviousWeekday(preparationDate);
        const preparationDateWeekday = getDateWeekday(preparationDate);
        preparationDelays += getDaysToPreviousWeekday(preparationDateWeekday);
    }

    const validationLimitDate = dayJsDeliveryDate
        .subtract(preparationDelays + 1, 'day') // 1 being modification deadline days before preparation
        .hour(17)
        .minute(30); // Orders are locked at 17h30 J-2 when passOrdersToReady runs
    const validationLimitDateIsPast = validationLimitDate.isBefore(dayjs());
    const timeToValidationDateAsString = validationLimitDate.locale('fr').fromNow(true);
    const isUnvalidatedFirstOrder = firstOrder && !hasBeenValidated;
    const statusInfo: Record<'Created' | 'InProgress' | 'Validated', string> = {
        Created:
            isPunctual || isUnvalidatedFirstOrder
                ? `Plus que ${timeToValidationDateAsString} pour valider votre commande`
                : `Automatiquement renouvelée dans ${timeToValidationDateAsString}`,
        InProgress:
            isPunctual || isUnvalidatedFirstOrder
                ? `Plus que ${timeToValidationDateAsString} pour valider votre commande`
                : `Plus que ${timeToValidationDateAsString}  pour valider vos modifications`,
        Validated: `Plus que ${timeToValidationDateAsString} pour modifier cette commande`,
    };
    const validState = state === 'Created' || state === 'InProgress' || state === 'Validated' ? state : 'Created';
    return {
        validationLimitDate,
        fullString: statusInfo[validState],
        validationLimitDateIsPast,
    };
}

export function getDateAddFromDate({ daysToAdd, date }: { daysToAdd: number; date: Date | string }): Date {
    return dayjs.utc(date).add(daysToAdd, 'days').toDate();
}

export function getDaysToNextWeekday(day: number): number {
    switch (day) {
        case 0: // Sunday to Thursday next weekday is day after (j+1)
        case 1:
        case 2:
        case 3:
        case 4:
            return 1;
        case 5:
            return 3; // On Friday, next weekday is Monday (j+3)
        case 6:
            return 2; // On Saturday, next weekday is Monday (j+2)
        default:
            throw new Error('Day input must be between 0-6');
    }
}

export function getFormattedDateAddFromDate({
    date,
    daysToAdd,
    isNewFormat,
}: {
    date: Date | string;
    daysToAdd: number;
    isNewFormat: any;
}): string {
    return dayjs
        .utc(date)
        .add(daysToAdd, 'days')
        .format(isNewFormat ? NEW_FORMAT : FORMAT);
}

interface WorkedDaysParameters {
    from?: Date;
    shouldStartFromAWorkedDay?: boolean;
}

function getDaysToWorkedDayInXWorkedDays(
    numberOfWorkedDays: number,
    parameters: WorkedDaysParameters,
    totemClosedDays: string[],
): number {
    const startDate = parameters?.from ?? new Date();
    const currentWeekDay = startDate.getDay();

    let days = 0;

    if (parameters?.shouldStartFromAWorkedDay) {
        // if we start from sunday or saterday it's not a weekday day so it's not worked.
        if ([0, 6].includes(currentWeekDay)) {
            days += getDaysToNextWeekday((currentWeekDay + days) % 7);
        }
        while (
            totemClosedDays.includes(
                getFormattedDateAddFromDate({ date: startDate, daysToAdd: days, isNewFormat: true }),
            )
        ) {
            days += getDaysToNextWeekday((currentWeekDay + days) % 7);
        }
    }

    for (let i = 0; i < numberOfWorkedDays; i++) {
        do {
            days += getDaysToNextWeekday((currentWeekDay + days) % 7);
        } while (
            totemClosedDays.includes(
                getFormattedDateAddFromDate({ date: startDate, daysToAdd: days, isNewFormat: true }),
            )
        );
    }

    return days;
}

export function getNextAvailableDeliveryDate(totemClosedDays: GetTotemClosedDaysQuery['getTotemClosedDays']): Date {
    const now = new Date();
    const parisHour = parseInt(
        new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: 'numeric', hour12: false }).format(now),
        10,
    );
    const parisMinute = parseInt(
        new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', minute: 'numeric' }).format(now),
        10,
    );
    const isPastCutoff = parisHour > 17 || (parisHour === 17 && parisMinute >= 30);
    const workedDaysToAdd = isPastCutoff ? 3 : 2;
    return getDateAddFromDate({
        daysToAdd: getDaysToWorkedDayInXWorkedDays(
            workedDaysToAdd,
            { shouldStartFromAWorkedDay: true },
            totemClosedDays,
        ),
        date: now,
    });
}
